import { func, number, object } from "prop-types";
import React, { useContext } from "react";
import { getPagerPages } from "./pagerMath.js";
import * as R from "ramda";
import cx from "classnames";
import useSearchValues from "../../../../common/useSearchValues.js";
import { getPageUrl, loadInPage } from "../../../../common/queryStringBuilder.js";
import { QueryStringContext, createStore } from "../../QueryStringContext/QueryStringContext.jsx";

const propTypes = {
  totalPages: number,
  currentPage: number,
  onPageChange: func,
  labels: object,
};
const defaultProps = {
  currentPage: 1,
  totalResults: 1,
};
const Pagination = (props) => {
  const { queryStringState, setQueryStringState, model } = useContext(QueryStringContext);
  const { searchValues } = useSearchValues();
  const { dictionary } = model;
  const labels = dictionary.pagination;
  const { totalResults, perPage } = props;

  const currentPage = Number(searchValues.page);

  const totalPages = (Number(totalResults) > Number(perPage)) ? Math.ceil(Number(totalResults)/Number(perPage)) : 1;

  const middlePages = getPagerPages({currentPage, totalPages, blockRange: 3, firstPage: 1});

  const shouldShowFirstDots = R.head(middlePages) >= 3;
  const shouldShowLastDots = totalPages - R.last(middlePages) > 1;
  const shouldShowFirstPage = R.head(middlePages) !== 1;
  const shouldShowLastPage = R.last(middlePages) !== totalPages;

  /**
   * Render the page.
   *
   * @param string label
   *   element label.
   * @param int pageNum
   *   The human page number.
   * @param string modifier
   *   Not a clue.
   *
   * @return string
   *   A pagination link.
   */
  function renderPage(label, pageNum, modifier = "") {
    const isCurrentPage = pageNum === currentPage + 1;
    const key = label + pageNum;
    const pageUrl = getPageUrl(pageNum, model, queryStringState);
    const styleLink = {
      color: '#000000',
      fontFamily: '"Greta Text", Georgia, "Times New Roman", Times, serif',
      fontSize: '14.4px',
    };
    const isDisabled = isCurrentPage || pageNum < 1 || pageNum > totalPages;

    const className = cx(
      "pagination__button",
      "btn",
      "btn--text",
      [`${modifier}`],
      {
        "btn--current": isCurrentPage,
        "btn--disabled": isDisabled,
      },
    );

    if (loadInPage()) {
      return isDisabled && !isCurrentPage ? (
        <button className={className} key={key} aria-disabled="true" disabled>
          <span>{label}</span>
        </button>
      ) : isCurrentPage ? (
        <button className={className} key={key} aria-disabled="true" aria-current="page" aria-label={`${labels.currentPageLabel} ${label}`}>
          <span>{label}</span>
        </button>
      ) : (
        <button className={className} key={key} onClick={() => setQueryStringState({ page: pageNum - 1 })}>
          <span>{label}</span>
        </button>
      );
    }
    else {
      return isDisabled && !isCurrentPage ? (
        <a href={pageUrl} style={styleLink} className={className} key={key} aria-disabled="true" disabled>
          <span>{label}</span>
        </a>
      ) : isCurrentPage ? (
        <a href={pageUrl} style={styleLink} className={className} key={key} aria-disabled="true" aria-current="page" aria-label={`${labels.currentPageLabel} ${label}`}>
          <span>{label}</span>
        </a>
      ) : (
        <a href={pageUrl} style={styleLink} className={className} key={key}>
          <span>{label}</span>
        </a>
      );
    }
  }

  return (
    <div className="search__pagination">
      <div className="pagination">
        <div
          className="pagination__pages"
          role="navigation"
          aria-label={labels.paginationLabel}
        >
          {currentPage !== 0 &&
            renderPage(
              <span>{labels.previous}</span>,
              currentPage,
              "btn--prev coh-style-slider-navigation-left pagination__page-prev",
            )}
          {shouldShowFirstPage && renderPage("1", 1)}
          {shouldShowFirstDots && <div className="pagination__dots">...</div>}
          {middlePages.map((page) => renderPage(page.toString(), page))}
          {shouldShowLastDots && <div className="pagination__dots">...</div>}
          {shouldShowLastPage && renderPage(totalPages.toString(), totalPages)}
          {currentPage + 1 !== totalPages &&
            renderPage(
              <span>{labels.next}</span>,
              currentPage + 2,
              "btn--next coh-style-slider-navigation-right pagination__page-next",
            )}
        </div>
      </div>
    </div>
  );
};

Pagination.defaultProps = defaultProps;
Pagination.propTypes = propTypes;
export default Pagination;
